import { useQuery } from "@tanstack/react-query";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, CircularProgress, Chip, Stack, Card, CardContent,
  useMediaQuery, useTheme, ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import { getAdminFeedback } from "../../../utility/httpActions/adminActions";
import useListQueryState from "../../../hooks/useListQueryState";

const TYPE_TABS = [
  { value: "", label: "All" },
  { value: "BUG", label: "Bug" },
  { value: "FEATURE_REQUEST", label: "Feature Request" },
  { value: "GENERAL", label: "General" },
];

const typeColor = { BUG: "error", FEATURE_REQUEST: "info", GENERAL: "default" };

const FeedbackList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, updateParams] = useListQueryState();
  const type = searchParams.get("type") || "";
  const page = Number(searchParams.get("page")) || 0;
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-feedback", type, page],
    queryFn: () => getAdminFeedback({ page: page + 1, limit, type: type || undefined }),
  });

  const handleTypeChange = (_, value) => {
    if (value === null) return;
    updateParams({ type: value, page: undefined });
  };

  const feedback = data?.feedback || [];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>Feedback</Typography>

      <ToggleButtonGroup
        value={type}
        exclusive
        onChange={handleTypeChange}
        size="small"
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        {TYPE_TABS.map((tab) => (
          <ToggleButton key={tab.value} value={tab.value}>{tab.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {isLoading ? (
        <CircularProgress />
      ) : feedback.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No feedback yet.</Typography>
      ) : isMobile ? (
        <Stack spacing={2}>
          {feedback.map((f) => (
            <Card key={f._id} variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Chip label={f.type} color={typeColor[f.type]} size="small" />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(f.createdAt).toLocaleDateString()}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1 }}>
                  {f.message}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {f.user?.name || "—"} {f.user?.email ? `(${f.user.email})` : ""}
                </Typography>
                {(f.platform || f.appVersion) && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {[f.platform, f.appVersion].filter(Boolean).join(" · ")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Platform</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedback.map((f) => (
              <TableRow key={f._id}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {new Date(f.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Chip label={f.type} color={typeColor[f.type]} size="small" />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {f.user?.name || "—"}
                  {f.user?.email && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {f.user.email}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: "pre-wrap", minWidth: 280 }}>{f.message}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {[f.platform, f.appVersion].filter(Boolean).join(" · ") || "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={(_, p) => updateParams({ page: p || undefined })}
        rowsPerPage={limit}
        rowsPerPageOptions={[limit]}
      />
    </Box>
  );
};

export default FeedbackList;
